import { request } from '@umijs/max';
import type { ApiPageResult, ApiSortItem } from '@/services/apiResponse';
import { getNebulithInferenceProApiUrl } from '@/services/util';
import type { EntitlementOperatorUser } from './entitlement';

export type ApiKeyStatus = 'active' | 'inactive' | 'manual_stopped';

export interface ApiKeyListItem {
  apiKeyId: string;
  createAt?: string;
  createBy?: string;
  createUser?: EntitlementOperatorUser | null;
  description?: string;
  keyName: string;
  secret?: string;
  showOrder?: number;
  status?: ApiKeyStatus;
  tenantId?: string;
  updateAt?: string;
  updateBy?: string;
  updateUser?: EntitlementOperatorUser | null;
  userId?: string;
}

export interface ApiKeyListPageParams {
  keyName?: string;
  status?: ApiKeyStatus;
  tenantId?: string;
  userId?: string;
  page: {
    pageNo: number;
    pageSize: number;
  };
  sorts?: ApiSortItem[];
}

export type ApiKeyListPageResult = ApiPageResult<ApiKeyListItem>;

export interface ApiKeyListParams {
  keyName?: string;
  status?: ApiKeyStatus;
  tenantId?: string;
  userId?: string;
}

export interface ApiKeyListResult {
  list: ApiKeyListItem[];
}

export interface CreateApiKeyParams {
  /** API-Key 名称，必填，唯一性 */
  keyName: string;
  /** 描述，非必填 */
  description?: string;
}

export interface UpdateApiKeyParams extends CreateApiKeyParams {
  /** API-Key 业务 ID */
  apiKeyId: string;
}

export interface DeleteApiKeyParams {
  apiKeyId: string;
}

export interface ApiKeyIdParams {
  apiKeyId: string;
}

/** API-Key 列表（无分页） */
export async function getApiKeyList(
  params?: ApiKeyListParams,
  options?: { skipErrorHandler?: boolean },
) {
  const res = await request<ApiKeyListResult | ApiKeyListItem[]>(
    getNebulithInferenceProApiUrl('api-key/list'),
    {
      method: 'POST',
      data: params ?? {},
      ...(options || {}),
    },
  );
  if (Array.isArray(res)) {
    return res;
  }
  return res?.list ?? [];
}

/** API-Key 分页列表 */
export async function getApiKeyListPage(
  params: ApiKeyListPageParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<ApiKeyListPageResult>(
    getNebulithInferenceProApiUrl('api-key/list-page'),
    {
      method: 'POST',
      data: params,
      ...(options || {}),
    },
  );
}

/** 创建 API-Key */
export async function createApiKey(
  params: CreateApiKeyParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('api-key/create'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 更新 API-Key */
export async function updateApiKey(
  params: UpdateApiKeyParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('api-key/update'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 删除 API-Key */
export async function deleteApiKey(
  params: DeleteApiKeyParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('api-key/delete'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 手动启动 API-Key：先置为 inactive，再按额度池重算状态 */
export async function startApiKey(
  params: ApiKeyIdParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('api-key/start'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 手动停用 API-Key：进入 manual_stopped，不再随额度池自动变更 */
export async function stopApiKey(
  params: ApiKeyIdParams,
  options?: { skipErrorHandler?: boolean },
) {
  return request<unknown>(getNebulithInferenceProApiUrl('api-key/stop'), {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}
